// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

const NEAR_INTEGER_EPS = 0.005;
const AMOUNT_DECIMALS = 2;
const AMOUNT_PRECISION = 10 ** AMOUNT_DECIMALS;

export const roundAmountTo2 = (value: number): number =>
  Math.round(value * AMOUNT_PRECISION) / AMOUNT_PRECISION;

/**
 * Rounds percentage to 2 decimals. Snaps to integer when within 0.005 to avoid
 * float drift (e.g. 100.004 from input showing as 100.01%).
 */
export const roundDestinationPercentageInput = (value: number): number => {
  const near = Math.round(value);
  if (Math.abs(value - near) < NEAR_INTEGER_EPS) return near;
  return Math.round(value * 100) / 100;
};

/**
 * Calculates the number of shares based on a percentage of total shares.
 *
 * @example
 *
 * ```ts
 * calculateSharesFromPercentage(50, 1000); // Returns 500
 * calculateSharesFromPercentage(25, 200); // Returns 50
 * ```
 *
 * @param percentage - The percentage value (0-100).
 * @param totalShares - The total number of shares available.
 * @returns The calculated number of shares.
 */
export const calculateSharesFromPercentage = (
  percentage: number,
  totalShares: number,
): number => {
  const value = (percentage / 100) * totalShares;
  return Math.round(value * 100) / 100;
};

/**
 * Calculates the amount based on a percentage of total amount.
 *
 * @example
 *
 * ```ts
 * calculateAmountFromPercentage(50, 10000); // Returns 5000
 * calculateAmountFromPercentage(25, 8000); // Returns 2000
 * ```
 *
 * @param percentage - The percentage value (0-100).
 * @param totalAmount - The total amount available.
 * @returns The calculated amount.
 */
export const calculateAmountFromPercentage = (
  percentage: number,
  totalAmount: number,
): number => {
  const value = (percentage / 100) * totalAmount;
  return Math.round(value * AMOUNT_PRECISION) / AMOUNT_PRECISION;
};

/**
 * Calculates the percentage based on shares relative to total shares.
 *
 * @example
 *
 * ```ts
 * calculatePercentageFromShares(500, 1000); // Returns 50
 * calculatePercentageFromShares(250, 1000); // Returns 25
 * calculatePercentageFromShares(100, 0); // Returns 0
 * ```
 *
 * @param shares - The number of shares.
 * @param totalShares - The total number of shares available.
 * @returns The calculated percentage (0-100). Returns 0 if totalShares is 0.
 */
export const calculatePercentageFromShares = (
  shares: number,
  totalShares: number,
): number => {
  if (totalShares === 0) return 0;
  const value = (shares / totalShares) * 100;
  return Math.round(value * 100) / 100;
};

/**
 * Calculates the percentage based on amount relative to total amount.
 *
 * @example
 *
 * ```ts
 * calculatePercentageFromAmount(5000, 10000); // Returns 50
 * calculatePercentageFromAmount(2000, 8000); // Returns 25
 * calculatePercentageFromAmount(1000, 0); // Returns 0
 * ```
 *
 * @param amount - The amount value.
 * @param totalAmount - The total amount available.
 * @returns The calculated percentage (0-100). Returns 0 if totalAmount is 0.
 */
export const calculatePercentageFromAmount = (
  amount: number,
  totalAmount: number,
): number => {
  if (totalAmount === 0) return 0;
  const value = (amount / totalAmount) * 100;
  return Math.round(value * 100) / 100;
};

/**
 * Calculates the destination fund amount based on a percentage of the refund
 * amount. This is used to distribute the refund amount across multiple destination
 * funds.
 *
 * @example
 *
 * ```ts
 * // If refund amount is 10000 and we want to allocate 30% to a fund:
 * calculateDestinationAmountFromPercentage(30, 10000); // Returns 3000
 * ```
 *
 * @param percentage - The percentage of the refund amount to allocate (0-100).
 * @param refundAmount - The total refund amount to be distributed.
 * @returns The calculated amount for the destination fund.
 */
export const calculateDestinationAmountFromPercentage = (
  percentage: number,
  refundAmount: number,
): number => {
  const base = Math.round(refundAmount * AMOUNT_PRECISION) / AMOUNT_PRECISION;
  const value = (percentage / 100) * base;
  return Math.round(value * AMOUNT_PRECISION) / AMOUNT_PRECISION;
};

/**
 * Returns the 2-decimal percentage that, when applied to the rounded base,
 * reproduces the given amount. Avoids drift (e.g. 50.01% when amount is 50% of
 * base).
 *
 * @param amount - The amount allocated to the destination fund.
 * @param refundAmount - The total refund amount (will be rounded for base).
 * @returns The percentage (0-100) with 2 decimals. 0 if refundAmount is 0.
 */
export const calculateDestinationPercentageFromAmount = (
  amount: number,
  refundAmount: number,
): number => {
  const base = Math.round(refundAmount * AMOUNT_PRECISION) / AMOUNT_PRECISION;
  if (base === 0) return 0;
  const exact = (amount / base) * 100;
  const rounded = Math.round(exact * 100) / 100;
  if (Math.round((rounded / 100) * base) === amount) return rounded;
  const alt = rounded - 0.01;
  if (Math.round((alt / 100) * base) === amount) return Math.max(0, alt);
  const altPlus = rounded + 0.01;
  return Math.min(100, altPlus);
};
